import React, { useState, useEffect } from 'react';
import styles from './FailedAIMsgs.module.css';
import { useSelector } from "react-redux";

const getToken = () => localStorage.getItem("token");
const backend_api_base_url = `${process.env.REACT_APP_SL_BE_BASE_ENV_PART}/${process.env.REACT_APP_STAGE_ENV}`;

export default function FailedAIMsgs({ appData, tenantPlan }) {
  const [failedQuestions, setFailedQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resolvingId, setResolvingId] = useState(null);
  const [answers, setAnswers] = useState({});
  const [resolvedIds, setResolvedIds] = useState([]);
  const [stats, setStats] = useState({ total: 0, resolved: 0 });
  const [filter, setFilter] = useState('unresolved');
  const [sortBy, setSortBy] = useState('failure_count');
  const [showAccessDenied, setShowAccessDenied] = useState(false);

  const kb_id = appData?.tenant_id ? `${appData.tenant_id}__${appData.app_name}` : 'tenant1__test';
  const meData = useSelector((state) => state.auth.meData);
  console.log("is my get meData form redux", meData);

  // Check if user is admin (tenant_type)
  const isAdminUser = meData?.type === "tenant_type";

  useEffect(() => {
    // Show popup if not admin
    if (meData && !isAdminUser) {
      setShowAccessDenied(true);
    } else {
      setShowAccessDenied(false);
    }
  }, [meData, isAdminUser]);

  useEffect(() => {
    if (isAdminUser) {
      fetchFailedQA();
    }
  }, [kb_id, filter, isAdminUser]);

  // Fetch failed QA questions - UPDATED to handle body as object OR string
  const fetchFailedQA = async () => {
    try {
      setLoading(true);
      setError(null);
      const region = appData?.region?.toLowerCase();

      const token = getToken();
      const resolvedParam = filter === 'resolved' ? 'true' : filter === 'unresolved' ? 'false' : '';

      // Build URL with query parameters
      let url = `https://${region}.autobot2.${backend_api_base_url}/ai_query_sys?task=qa_failed&tenant_id=${kb_id}&limit=50`;

      if (resolvedParam !== '') {
        url += `&resolved=${resolvedParam}`;
      }

      if (filter === 'all') {
        // For 'all', we don't filter by resolved status
        // Just fetch without resolved parameter
        url = `https://${region}.autobot2.${backend_api_base_url}/ai_query_sys?task=qa_failed&tenant_id=${kb_id}&limit=100`;
      }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Failed QA response:', result);

      // ---- UPDATED: unwrap body whether it's a string OR an object ----
      let data = result;

      if (result && result.body !== undefined) {
        if (typeof result.body === 'string') {
          try {
            data = JSON.parse(result.body);
          } catch (parseError) {
            console.error('Error parsing QA response:', parseError);
            throw new Error('Invalid response format');
          }
        } else {
          // body is already an object (this is your current case)
          data = result.body;
        }
      }

      // Check if data is null or undefined
      if (!data) {
        throw new Error('Empty response received');
      }

      // Check if the data has the expected structure
      if (data.status === 'ok') {
        const questions = data.questions || []; // Ensure it's always an array
        setFailedQuestions(questions);
        setStats({
          total: data.total || 0,
          resolved: questions.filter(q => q.resolved).length || 0
        });
        // Clear resolvedIds when fetching new data
        setResolvedIds([]);
      } else {
        throw new Error(data.message || data.error || 'Failed to fetch data');
      }
    } catch (err) {
      console.error("Error fetching failed QA questions:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (id, value) => {
    setAnswers(prev => ({ ...prev, [id]: value }));
  };

  const resolveQuestion = async (id) => {
    const answer = answers[id];
    if (!answer?.trim()) {
      alert('Please enter an answer');
      return;
    }

    try {
      setResolvingId(id);
      const token = getToken();

      // Use the proxy endpoint for resolving as well
      const response = await fetch(
        `${process.env.REACT_APP_HF_INGEST_URL}/ai_query_sys?task=qa_resolve&question_id=${id}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            answer: answer.trim(),
            resolved_by: appData?.tenant_info?.full_name || 'admin'
          })
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Resolved response:', result);

      // ---- UPDATED: unwrap body whether it's a string OR an object ----
      let data = result;

      if (result && result.body !== undefined) {
        if (typeof result.body === 'string') {
          try {
            data = JSON.parse(result.body);
          } catch (parseError) {
            console.error('Error parsing resolve response:', parseError);
            throw new Error('Invalid response format');
          }
        } else {
          // body is already an object
          data = result.body;
        }
      }

      // Check if data is null or undefined
      if (!data) {
        throw new Error('Empty response received');
      }

      // Check if the resolve was successful
      if (data.status === 'success' || data.status === 'ok') {
        // Add to resolvedIds for temporary UI feedback
        setResolvedIds(prev => [...prev, id]);
        setAnswers(prev => {
          const newAnswers = { ...prev };
          delete newAnswers[id];
          return newAnswers;
        });

        // Refresh the list after resolving
        setTimeout(() => {
          fetchFailedQA();
        }, 1000);
      } else {
        throw new Error(data.message || data.error || 'Failed to resolve question');
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
      console.error('Error resolving question:', err);
    } finally {
      setResolvingId(null);
    }
  };

  const getSortedQuestions = () => {
    const sorted = [...failedQuestions];

    if (sortBy === 'failure_count') {
      sorted.sort((a, b) => (b.failure_count || 0) - (a.failure_count || 0));
    } else if (sortBy === 'confidence') {
      sorted.sort((a, b) => (a.confidence || 0) - (b.confidence || 0));
    } else if (sortBy === 'date') {
      sorted.sort((a, b) => new Date(b.last_failed_at || b.created_at) - new Date(a.last_failed_at || a.created_at));
    }

    return sorted;
  };

  // Access Denied Popup
  if (showAccessDenied) {
    return (
      <div className={styles.accessDeniedOverlay}>
        <div className={styles.accessDeniedPopup}>
          <div className={styles.accessDeniedIcon}>🔒</div>
          <h3 className={styles.accessDeniedTitle}>Access Denied</h3>
          <p className={styles.accessDeniedMessage}>
            Only administrators (tenant_type users) can view and resolve failed questions.
          </p>
          <div className={styles.accessDeniedUserInfo}>
            <strong>Your user type:</strong> {meData?.type || 'Unknown'}
          </div>
          <button
            className={styles.accessDeniedButton}
            onClick={() => setShowAccessDenied(false)}
          >
            Got it
          </button>
        </div>
      </div>
    );
  }

  // Non-admin view (empty state with message)
  if (!isAdminUser) {
    return (
      <div className={styles.nonAdminContainer}>
        <div className={styles.nonAdminIcon}>👋</div>
        <h3 className={styles.nonAdminTitle}>Welcome to Failed Questions Review</h3>
        <p className={styles.nonAdminMessage}>
          This section is only accessible to administrators. If you need to review or resolve failed questions, please contact your administrator.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <div className={styles.loadingText}>Loading failed questions...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorIcon}>❌</div>
        <div className={styles.errorMessage}>Error: {error}</div>
        <button onClick={fetchFailedQA} className={styles.retryButton}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Failed Questions Review</h2>
        <div className={styles.filterTabs}>
          <button
            onClick={() => setFilter('unresolved')}
            className={`${styles.filterButton} ${filter === 'unresolved' ? styles.filterActive : ''}`}
          >
            Unresolved ({stats.total - stats.resolved})
          </button>
          <button
            onClick={() => setFilter('resolved')}
            className={`${styles.filterButton} ${filter === 'resolved' ? styles.filterActive : ''}`}
          >
            Resolved ({stats.resolved})
          </button>
          <button
            onClick={() => setFilter('all')}
            className={`${styles.filterButton} ${filter === 'all' ? styles.filterActive : ''}`}
          >
            All ({stats.total})
          </button>
        </div>
      </div>

      {filter !== 'resolved' && failedQuestions.length > 0 && (
        <div className={styles.sortControls}>
          <span className={styles.sortLabel}>Sort by:</span>
          <button
            onClick={() => setSortBy('failure_count')}
            className={`${styles.sortButton} ${sortBy === 'failure_count' ? styles.sortActive : ''}`}
          >
            🔥 Most Failed
          </button>
          <button
            onClick={() => setSortBy('confidence')}
            className={`${styles.sortButton} ${sortBy === 'confidence' ? styles.sortActive : ''}`}
          >
            📉 Lowest Confidence
          </button>
          <button
            onClick={() => setSortBy('date')}
            className={`${styles.sortButton} ${sortBy === 'date' ? styles.sortActive : ''}`}
          >
            🕒 Most Recent
          </button>
        </div>
      )}

      {failedQuestions.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyStateIcon}>📭</div>
          <div className={styles.emptyStateText}>
            {filter === 'resolved' ? 'No resolved questions found' :
              filter === 'unresolved' ? 'No unresolved questions found' :
                'No questions found'}
          </div>
        </div>
      ) : (
        getSortedQuestions().map(q => {
          // Check if this question was just resolved
          const isJustResolved = resolvedIds.includes(q.id);
          const isResolved = q.resolved || isJustResolved;

          return (
            <div
              key={q.id}
              className={`${styles.questionCard} ${isResolved ? styles.resolvedCard : ''}`}
            >
              {!isResolved && q.failure_count > 1 && (
                <div className={styles.failureBadge}>
                  Failed {q.failure_count} times
                </div>
              )}

              <div className={styles.questionText}>{q.question}</div>

              {q.attempted_answer && !isResolved && (
                <div className={styles.attemptedAnswer}>
                  <strong>Bot attempted:</strong> {q.attempted_answer}
                </div>
              )}

              {q.resolved_answer && (
                <div className={styles.resolvedAnswer}>
                  <strong className={styles.resolvedAnswerStrong}>✓ Correct answer:</strong> {q.resolved_answer}
                  <div className={styles.resolvedMeta}>
                    Resolved by {q.resolved_by} on {new Date(q.resolved_at).toLocaleString()}
                  </div>
                </div>
              )}

              {isJustResolved && (
                <div className={styles.justResolvedBadge}>
                  ✅ Resolved successfully!
                </div>
              )}

              <div className={styles.metadata}>
                <span>Confidence: <strong>{(q.confidence * 100).toFixed(1)}%</strong></span>
                {!isResolved && q.failure_count > 0 && (
                  <span>Failed: <strong className={styles.failureCount}>{q.failure_count}x</strong></span>
                )}
                <span>Last: {new Date(q.last_failed_at || q.created_at).toLocaleString()}</span>
                {q.metadata?.signal === 2 && (
                  <span className={styles.lowConfidenceBadge}>⚠️ Low confidence</span>
                )}
                {isResolved && (
                  <span className={styles.resolvedBadge}>✓ Resolved</span>
                )}
              </div>

              {!isResolved && filter !== 'resolved' && (
                <div className={styles.answerSection}>
                  <textarea
                    value={answers[q.id] || ''}
                    onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                    placeholder="Enter the correct answer..."
                    className={styles.answerInput}
                    disabled={resolvingId === q.id}
                  />
                  <button
                    onClick={() => resolveQuestion(q.id)}
                    disabled={resolvingId === q.id || !answers[q.id]?.trim()}
                    className={styles.submitButton}
                  >
                    {resolvingId === q.id ? 'Submitting...' : 'Submit Answer'}
                  </button>
                </div>
              )}
            </div>
          );
        })
      )}

      {failedQuestions.length > 0 && (
        <div className={styles.refreshContainer}>
          <button onClick={fetchFailedQA} className={styles.refreshButton}>
            Refresh List
          </button>
        </div>
      )}
    </div>
  );
}